import * as stylex from '@stylexjs/stylex';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { ReactElement } from 'react';

import {
  Breadcrumb,
  Breadcrumbs,
  Heading,
  Image,
  Paragraph,
  View,
} from '@/libs/serenity-ui';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { RouterLink } from '@/libs/tanstack-router';

export const Route = createFileRoute('/_main/_details/campaigns/create/guide/')({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    if (context.user && !context.can.campaignRequests.view()) {
      throw redirect({ to: '/' });
    }
  },
  staticData: {
    details: {
      heading: 'Create campaigns',
      slots: {
        topBarComponent: TopBarComponent,
      },
    },
  },
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Campaigns — Create campaigns',
      },
    ],
  }),
});

function RouteComponent(): ReactElement {
  return (
    <article {...stylex.props(articleStyles.base)}>
      <View style={introSectionStyles.base}>
        <View style={contextParagraphStyles.base}>
          <Paragraph color="secondary" size={16}>
            TrendMD dashboard lets you request new campaigns by submitting campaign data. After you submit a campaign,
            we review the provided data and set up the campaign for you.
          </Paragraph>
          <Paragraph color="secondary" size={16}>
            This document describes how to create a campaign in the dashboard.
          </Paragraph>
        </View>
      </View>

      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={2} size={24} weight="medium">Create a campaign</Heading>
        <ol {...stylex.props(orderedListStyles.base)}>
          <li {...stylex.props(orderedListStyles.item)}>
            In the dashboard, go to <strong {...stylex.props(textStyles.primary)}>Campaigns</strong>.
            <br />
            This opens the <strong {...stylex.props(textStyles.primary)}>Campaigns</strong> page.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            In the top-right corner, click <strong {...stylex.props(textStyles.primary)}>Create campaign</strong>{' '}
            (<strong {...stylex.props(textStyles.primary)}>+</strong>).
            <br />
            This opens the <strong {...stylex.props(textStyles.primary)}>Add campaign</strong> page.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            For <strong {...stylex.props(textStyles.primary)}>Name</strong>, enter a descriptive name for the campaign.
            <br />
            Recommended format: Campaign Name Month Year.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            Optional: For <strong {...stylex.props(textStyles.primary)}>Custom ID</strong>, enter the ID of the
            campaign in your system.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            For <strong {...stylex.props(textStyles.primary)}>Targeting type</strong>, select one of the following:
            <ul {...stylex.props(nestedListStyles.base)}>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>NPI</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : Target specific healthcare professionals by National Provider Identifiers (NPIs). You&apos;ll need
                  to provide the NPI of each specific professional to target. A campaign with this targeting type will
                  run only in United States.
                </span>
              </li>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>Geo</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : Target healthcare professionals in specific locations, such as a country or a US state.
                </span>
              </li>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>Specialty</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : Target healthcare professionals of specific medical specialties. This identifies look-a-like
                  patterns and targets professionals that are likely to be a part of the specified specialties.
                </span>
              </li>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>Specialty-auth</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : Target healthcare professionals of specific medical specialties. This identifies authenticated
                  professionals of specified specialties.
                </span>
              </li>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>RoN</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : Targets all readers in the network. Provides the widest reach but may result in low relevance.
                </span>
              </li>
            </ul>
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            Depending on the selected <strong {...stylex.props(textStyles.primary)}>Targeting type</strong>, do one
            of the following:
            <ul {...stylex.props(nestedListStyles.base)}>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>NPI</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : For <strong {...stylex.props(textStyles.primary)}>NPI list</strong>, upload a csv file with a
                  single column containing NPIs.
                </span>
              </li>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>Geo</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : For <strong {...stylex.props(textStyles.primary)}>Geo</strong>, enter one or more countries or US
                  states to target, separated by commas.
                </span>
              </li>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>Specialty</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  {' '}and <strong {...stylex.props(textStyles.primary)}>Specialty-auth</strong>
                  : For <strong {...stylex.props(textStyles.primary)}>Specialty</strong>, enter the specialties to
                  target directly in the designated field, separated by commas.
                </span>
              </li>
            </ul>
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            For <strong {...stylex.props(textStyles.primary)}>Content file</strong>, upload a CSV file with a list of
            articles to promote in this campaign.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            For <strong {...stylex.props(textStyles.primary)}>Budget</strong>, enter the sum you want to spend for this
            campaign.
            <br />
            This value can be calculated automatically if you enter CPC and click goals instead.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            For <strong {...stylex.props(textStyles.primary)}>CPC</strong>, enter the cost per click for this campaign.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            For <strong {...stylex.props(textStyles.primary)}>Click goals</strong>, enter the number of clicks this
            campaign needs to reach.
            <br />
            This value can be calculated automatically if you enter CPC and budget instead.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            Select or enter <strong {...stylex.props(textStyles.primary)}>Start date</strong> and{' '}
            <strong {...stylex.props(textStyles.primary)}>End date</strong>.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            For <strong {...stylex.props(textStyles.primary)}>Distribution</strong>, select one of the following:
            <ul {...stylex.props(nestedListStyles.base)}>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>Evenly</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : Distribute the budget across the entire duration of the campaign
                </span>
              </li>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>Front-loaded</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : Spend the budget as quickly as possible while meeting the targeting criteria
                </span>
              </li>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>Custom monthly</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : Define a custom monthly budget for each month of the campaign
                </span>
              </li>
            </ul>
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            For <strong {...stylex.props(textStyles.primary)}>Custom monthly</strong> distribution: Enter the budget
            for each month.
            <br />
            After entering the budgets, you&apos;ll see the number of clicks for each month.
            <br />
            The sum of the per-month budgets should be equal to the total budget you&apos;ve entered in the{' '}
            <strong {...stylex.props(textStyles.primary)}>Budget</strong> field.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            Optional: For <strong {...stylex.props(textStyles.primary)}>Owner</strong>, select the user responsible for
            the campaign. That user will be able to manage the campaign and receive notifications about it.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            Optional: For <strong {...stylex.props(textStyles.primary)}>Team</strong>, select the team responsible for
            the campaign.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            Click one of the following to finalize:
            <ul {...stylex.props(nestedListStyles.base)}>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>Save</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : Save the campaign as a draft to come back to later.
                </span>
              </li>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>Submit</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : Submit the campaign for review. You will get a notification when the campaign gets approved. If
                  changes or additional information is needed, you&apos;ll get a message.
                </span>
              </li>
            </ul>
          </li>
        </ol>
      </section>

      <Image
        alt="Add campaign form"
        src="/campaigns/create-campaign-form-guide.webp"
        style={screenshotStyles.base}
      />

      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={2} size={24} weight="medium">Campaign statuses</Heading>
        <ul {...stylex.props(statusListStyles.base)}>
          <li {...stylex.props(statusListStyles.item)}>
            <span {...stylex.props(statusListStyles.description)}>
              Draft: Campaign was saved but not submitted for review. You can edit and submit for review.
            </span>
          </li>
          <li {...stylex.props(statusListStyles.item)}>
            <span {...stylex.props(statusListStyles.description)}>
              In review: Campaign was sent for review. You can edit the campaign.
            </span>
          </li>
          <li {...stylex.props(statusListStyles.item)}>
            <span {...stylex.props(statusListStyles.description)}>
              Processing: We&apos;re reviewing the campaign. The campaign can&apos;t be edited.
            </span>
          </li>
          <li {...stylex.props(statusListStyles.item)}>
            <span {...stylex.props(statusListStyles.description)}>
              Active: The campaign is running. If edits are needed, you can submit an edit request.
            </span>
          </li>
          <li {...stylex.props(statusListStyles.item)}>
            <span {...stylex.props(statusListStyles.description)}>
              Completed: The campaign liquidated 100% of the budget or click goals. You can submit edit requests.
            </span>
          </li>
          <li {...stylex.props(statusListStyles.item)}>
            <span {...stylex.props(statusListStyles.description)}>
              Paused: You requested to pause the campaign, and it&apos;s not running. You can resume the campaign by
              submitting an edit request.
            </span>
          </li>
          <li {...stylex.props(statusListStyles.item)}>
            <span {...stylex.props(statusListStyles.description)}>
              Ended: The campaign&apos;s end date passed, so the campaign can no longer run or be edited.
            </span>
          </li>
          <li {...stylex.props(statusListStyles.item)}>
            <span {...stylex.props(statusListStyles.description)}>
              Declined: Campaign can&apos;t run with the current parameters. Check the decline reason, and submit a new
              campaign with updated parameters. To avoid re-submitting all campaign data again, we recommend duplicating
              the declined campaign and updating the needed parameters.
            </span>
          </li>
        </ul>
      </section>
    </article>
  );
}

function TopBarComponent(): ReactElement {
  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/campaigns">Campaigns</RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink to="/campaigns/create/guide">Create campaigns</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

const articleStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 88,
    justifySelf: 'center',
    maxWidth: 728,
    minWidth: 0,
    width: '100%',
  },
});

const introSectionStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
  },
});

const sectionStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
});

const contextParagraphStyles = stylex.create({
  base: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    borderLeftColor: $.borderBlack,
    borderLeftStyle: 'solid',
    borderLeftWidth: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    justifyContent: 'center',
    paddingLeft: 24,
  },
});

const orderedListStyles = stylex.create({
  base: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
    margin: 0,
    paddingLeft: 20,
  },
  item: {
    marginBottom: 0,
  },
});

const nestedListStyles = stylex.create({
  base: {
    marginBlock: 0,
    paddingLeft: 20,
  },
  item: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
    listStyle: 'disc',
  },
  description: {
    color: $.textSecondary,
    fontWeight: 400,
  },
});

const statusListStyles = stylex.create({
  base: {
    margin: 0,
    paddingLeft: 20,
  },
  item: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
  },
  description: {
    color: $.textSecondary,
    fontWeight: 400,
  },
});

const textStyles = stylex.create({
  primary: {
    color: $.textPrimary,
    fontWeight: 600,
  },
});

const screenshotStyles = stylex.create({
  base: {
    aspectRatio: '1 / 1',
    objectFit: 'cover',
    width: '100%',
  },
});
